/**
 * Locale stays fixed (digit grouping/decimal style); currency is per-org
 * (Organization.currency, defaults ZAR). Accepts a string because billing
 * Decimal fields come back from the API as strings (Pydantic's default
 * JSON encoding, to avoid float precision loss) -- never treat those as
 * numbers directly.
 */
export function formatCurrency(amount: number | string | null, currency: string): string {
  if (amount === null) return '—'
  const value = typeof amount === 'string' ? parseFloat(amount) : amount
  if (Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(value)
}

/** rate is a 0-1 fraction (or null when there's no data to compute a rate from). */
export function formatPercent(rate: number | null): string {
  if (rate === null) return '—'
  return new Intl.NumberFormat('en-ZA', { style: 'percent', maximumFractionDigits: 1 }).format(rate)
}

/**
 * A duration in seconds (e.g. WhatsApp read latency), rendered as
 * whichever of minutes/hours is more readable -- under an hour shows
 * minutes, otherwise hours to one decimal. null means "no data", not
 * "instant" -- see analytics.py's _whatsapp_read_latency_by_hour.
 */
export function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—'
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  return `${(seconds / 3600).toFixed(1)}h`
}

/**
 * Turn a human-typed template name into the `^[a-z0-9_]+$` the API
 * requires (routes_message_templates.py's CreateTemplateRequest).
 *
 * Exists so someone can type "Payment reminder" and get
 * "payment_reminder" rather than a disabled button and no explanation.
 * Returns "" when there is nothing usable left, which callers treat as
 * "no name yet" rather than submitting an empty string.
 */
export function slugifyTemplateName(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    // Collapse runs and trim the underscores those replacements leave at
    // the ends, so "Payment reminder!" doesn't become "payment_reminder_".
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
}

/** Every time on the site is shown in South African time. */
export const DISPLAY_TIME_ZONE = 'Africa/Johannesburg'

/**
 * The API sends UTC, often without a "Z" (naive timestamps). `new Date()`
 * would read those as the browser's local time, so add the "Z" first.
 * Date-only values ("2026-10-25") already parse as UTC midnight.
 */
export function parseApiDate(iso: string | Date): Date {
  if (iso instanceof Date) return iso
  const hasTime = /T\d{2}:\d{2}/.test(iso)
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(iso)
  return new Date(hasTime && !hasZone ? `${iso}Z` : iso)
}

/** "25/10/2026, 14:05" in Johannesburg time. */
export function formatDateTime(iso: string | Date): string {
  return parseApiDate(iso).toLocaleString('en-ZA', { timeZone: DISPLAY_TIME_ZONE })
}

/** "25/10/2026" in Johannesburg time; pass options for "25 October 2026" etc. */
export function formatDate(iso: string | Date, options?: Intl.DateTimeFormatOptions): string {
  return parseApiDate(iso).toLocaleDateString('en-ZA', { timeZone: DISPLAY_TIME_ZONE, ...options })
}

/** Today's calendar date ("2026-10-25") in Johannesburg, for date inputs. */
export function todayInJohannesburg(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: DISPLAY_TIME_ZONE }).format(new Date())
}

/** "5m ago" / "3h ago" / "2d ago" -- shared by the per-org WhatsApp
 * submission panel and the admin WhatsApp submissions directory, so the
 * two views of the same underlying timestamp always read the same way. */
export function formatRelative(iso: string): string {
  const diffMs = Date.now() - parseApiDate(iso).getTime()
  const minutes = Math.round(diffMs / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  return `${days}d ago`
}
